import React, { useEffect, useRef, useState } from 'react';
import { isQtyBearing, unitSuffix, defaultQtyFor } from '../../utils/addonUnits';
import { Box, Typography, TextField, Select, MenuItem, Button, ToggleButtonGroup, ToggleButton, Checkbox, FormControlLabel } from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import * as XLSX from 'xlsx';
import { parseTnbBill } from '../../utils/tnbBill';
import { parseTnbRows, analyzeLoadProfile } from '../../utils/cscLoadProfile';
import useRscPricing, { commercialPricePerKwp, parseInverterKw } from '../../hooks/useRscPricing';
import useCscBillProposal, {
    calcCommercialLvBill, calcCommercialMvBill, mvSolarRate, lvFlatSolarRate, isMvTariff, estimateMonthlySaving, estimateMonthlySavingSELCo,
    inverterFromKwp, inverterModelAtLeast, inverterModelAtMost, RATIO_CAP_NO_BATT, getCommercialPrice, commercialInverterUpgrade, exceedsHybridMax, BRAND_STRING_ADJ_SMALL,
} from '../../hooks/useCscBillProposal';
import { getAddons } from '../../api/resourceApi';
import { PER_BATTERY_PROMO } from '../../utils/rscPromos';
import { sstOn } from '../../utils/sst';
import { ccFromCash } from '../../utils/ccPricing';
import {
    batteryCostFor, setLvBattUnitNet, nextBattStep, prevBattStep, isHvBattery,
    BATT_BRAND_GOODWE, BATT_BRAND_HUAWEI, snapBattToBrand,
    omForKwp, snapNear96, billTargetCard, fillPanelsForInverter,
} from '../../utils/cscSizing';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

// ─────────────────────────────────────────────────────────────────────────
// CscMeterRow — ONE meter (project) of the Multi-Meter CSC tab.
//
// This component runs the EXACT single-meter CSC "Option B" (with-battery)
// calculation, lifted VERBATIM from cscCombinedTab.jsx, for a single meter's
// {bill, maxDemand, tariff} + the shared Section-1 settings (operating days /
// hours / daytime % / phase / brand). It reports the computed result up via
// `onResult` so the parent can combine N meters' totals.
//
// Math parity: the sizing (useCscBillProposal → optB), battery auto-pick,
// pricing (cardPricing), and saving/ROI (optBDisplay) blocks below are copied
// 1:1 from cscCombinedTab so a 1-meter result is identical to the single-meter
// Calculator tab for the same inputs. No CSC math is reinvented here.
// ─────────────────────────────────────────────────────────────────────────

const PP = "'Poppins', sans-serif";
const tr = (lang, en, zh, ms) => (lang === 'zh' ? zh : (lang === 'ms' && ms ? ms : en));

// Meter label uses LETTERS (A, B, C…) keyed off the meter's real index.
const meterLetter = (i) => String.fromCharCode(65 + (Number(i) || 0));

/**
 * What this meter is called. A typed label wins; otherwise the lettered default.
 *
 * Exported because the name is printed in six places — this card, the WhatsApp summary,
 * three quotation line-item names and the comparison table — and a rename that reached
 * only some of them would put "Factory Block 1" on screen and "Meter A" on the customer's
 * quotation. One helper, so there is one answer. (user 2026-10-14)
 *
 * Line items pass lang 'en' deliberately: a quotation is a document, not a UI, and should
 * not change its item names because somebody switched the calculator to Chinese.
 */
export const meterName = (meter, i, lang = 'en') => {
    const custom = String(meter?.label ?? '').trim();
    if (custom) return custom;
    return lang === 'cn' ? `\u7535\u8868 ${meterLetter(i)}` : `Meter ${meterLetter(i)}`;
};

// Parse a catalog addon's price into { base, perUnit, total } for a given qty.
// Copied VERBATIM from cscCombinedTab.jsx — the "+" base+rate syntax
// ("700+170" = RM 700 setup + RM 170 per unit) is honored FIRST, regardless of
// unitType, so a stale/missing unitType can't concatenate the digits.
function computeAddonAmount(addon, qty) {
    if (!addon) return { base: 0, perUnit: 0, total: 0 };
    const raw = String(addon.price ?? '');
    const cleaned = raw.replace(/[^\d.+]/g, '');
    if (cleaned.includes('+')) {
        const [b, p] = cleaned.split('+');
        const base = parseFloat(b) || 0;
        const perUnit = parseFloat(p) || 0;
        return { base, perUnit, total: base + perUnit * (Number(qty) || 0) };
    }
    const qtyBearing = isQtyBearing(addon.unitType);
    if (qtyBearing) {
        const perUnit = parseFloat(cleaned) || 0;
        return { base: 0, perUnit, total: perUnit * (Number(qty) || 0) };
    }
    // Preserve a leading minus so DISCOUNT rows (Solis −2000 / Huawei −500 in the residential band)
    // subtract instead of adding. (user 2026-08-07)
    const mag = parseFloat(raw.replace(/[^\d.]/g, '')) || 0;
    const num = raw.trim().startsWith('-') ? -mag : mag;
    return { base: 0, perUnit: num, total: num };
}

// Sum a meter's picked catalog add-on rows (mirrors cscCombinedTab.addonTotalForKey).
const addonRowsTotal = (rows) => (rows || []).reduce((sum, a) => {
    const qty = (isQtyBearing(a.unitType))
        ? (a._qty != null && a._qty !== '' && Number.isFinite(Number(a._qty)) ? Math.max(0, Number(a._qty)) : 1) : 1;
    return sum + computeAddonAmount(a, qty).total;
}, 0);

const COLORS = {
    primary: '#1A8CFF', dark: '#0F62CC', light: '#E8F4FF', bg: '#F8FAFC', card: '#FFFFFF',
    text: '#0F172A', sub: '#64748B', border: '#E2E8F0', success: '#10B981', warning: '#F59E0B',
};

const TARIFFS = [
    { rate: 0.515, label: 'Low Voltage (LV)' },
    // Time-of-Use LV: peak/off-peak energy, capacity and network per kWh, and a
    // rebate line. Priced by calcCommercialLvTouBill, never bill ÷ rate.
    { rate: 0.4539, label: 'Low Voltage ToU (LV ToU)' },
    { rate: 0.303, label: 'Medium Voltage (MV)' },
    { rate: 0.437, label: 'High Voltage (HV)' },
    { rate: 0.451, label: 'Agriculture' },
];

const fmtRM = (n) => 'RM ' + Math.round(Number(n) || 0).toLocaleString('en-MY');

// batteryCostFor / BATT_STEPS / nextBattStep / prevBattStep / isHvBattery / MAINT_TIERS /
// omForKwp now live in ../../utils/cscSizing (shared with cscCombinedTab.jsx — see import above).

const Label = ({ children }) => (
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: COLORS.sub, mb: 0.4, fontFamily: PP }}>{children}</Typography>
);

// Compact label + input style for the per-meter editable System Details (light card).
const MiniLabel = ({ children }) => (
    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: COLORS.sub, mb: 0.3, fontFamily: PP, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{children}</Typography>
);
const MINI_INPUT_SX = { '& .MuiInputBase-root': { fontFamily: PP, fontSize: '0.8rem', borderRadius: '6px' }, '& .MuiInputBase-input': { py: 0.6 } };

const Spec = ({ label, value }) => (
    <Box sx={{ textAlign: 'center', px: 1 }}>
        <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: PP }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#fff', fontFamily: "'JetBrains Mono',monospace" }}>{value}</Typography>
    </Box>
);

export default function CscMeterRow({
    index, meter, count, lang = 'en',
    // shared Section-1 settings (same across all meters)
    operatingDays, opStart, opEnd, dayUsagePct, phaseMode, brand,
    onChange, onRemove, onResult, onLpAnalysis,
}) {
    // Optional per-meter TNB uploads: bill PDF (auto-fill bill/MD/tariff) + 30-min
    // load-profile CSV/Excel (real usage pattern). The parent combines the patterns.
    const [uploadMsg, setUploadMsg] = useState('');
    const maxDemand = meter.maxDemand;
    const tariffRate = Number(meter.tariffRate) || 0.515;
    // Medium Voltage — same model as the single-meter card (cscCombinedTab): the meter keys
    // peak / off-peak kWh + MD, the RM bill is CALCULATED, and sizing / saving run on the
    // energy-only view (calcBill = kWh × calcRate) so bill ÷ rate recovers the true kWh.
    // LV / Agriculture unchanged. (user 2026-09-23)
    const isMv = isMvTariff(tariffRate);
    const _num = (v) => parseFloat(String(v ?? '').replace(/,/g, '')) || 0;
    const mvCtx = { peakKwh: _num(meter.mvPeakKwh), offKwh: _num(meter.mvOffKwh), mdKw: _num(maxDemand) };
    const mvKwh = mvCtx.peakKwh + mvCtx.offKwh;
    const mvBillCalc = isMv ? calcCommercialMvBill(mvCtx) : null;
    // Both branches fold AFA in, matching cscCombinedTab — these two are twins and a
    // sizing rule that holds in one and not the other is the oldest bug in this file.
    const calcRate = isMv ? mvSolarRate() : lvFlatSolarRate(tariffRate);
    const bill = isMv ? (mvBillCalc?.total > 0 ? String(Math.round(mvBillCalc.total)) : '') : meter.bill;
    const calcBill = isMv ? (mvKwh > 0 ? String(Math.round(mvKwh * calcRate)) : '') : bill;
    const billFn = (kwh) => (isMv ? calcCommercialMvBill(mvCtx, kwh) : calcCommercialLvBill(kwh, tariffRate));
    // Monthly usage (kWh) interlocked with the RM bill via the tariff (bill = kWh × tariff).
    // RM bill stays the calc source; billKwh is a convenience input. Legacy meters with no
    // billKwh derive it from the bill so the field is never blank when a bill exists.
    // AFA-aware, exactly as in cscCombinedTab: both take the RAW tariff and apply AFA
    // inside, so every call site keeps passing `tariffRate` unchanged.
    const _kwhFromBill = (rm, t) => { const r = lvFlatSolarRate(t); return (Number(rm) > 0 && r > 0 ? String(Math.round(Number(rm) / r)) : ''); };
    const _billFromKwh = (kwh, t) => { const r = lvFlatSolarRate(t); return (Number(kwh) > 0 && r > 0 ? String(Math.round(Number(kwh) * r)) : ''); };
    const billKwh = (meter.billKwh != null && meter.billKwh !== '') ? meter.billKwh : _kwhFromBill(bill, tariffRate);

    // ── Optional per-meter uploads ──
    const handleTnbUpload = async (file) => {
        if (!file) return;
        setUploadMsg(tr(lang, 'Reading TNB bill…', '读取账单…'));
        try {
            const buf = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
            let text = '';
            for (let p = 1; p <= pdf.numPages; p++) { const pg = await pdf.getPage(p); const c = await pg.getTextContent(); text += ' ' + c.items.map(i => i.str).join(' '); }
            const r = parseTnbBill(text);
            const rate = r.tariffRate || tariffRate;
            const patch = { ...meter, tariffRate: rate };
            if (isMvTariff(rate) && (r.peakKwh || r.offKwh)) {
                // MV: bill is built from the meter's peak / off-peak kWh + MD. (user 2026-09-23)
                patch.mvPeakKwh = r.peakKwh ? String(Math.round(r.peakKwh)) : '';
                patch.mvOffKwh = r.offKwh ? String(Math.round(r.offKwh)) : '';
            } else if (r.monthlyUsage) { patch.billKwh = String(Math.round(r.monthlyUsage)); patch.bill = _billFromKwh(r.monthlyUsage, rate); }
            if (r.md) patch.maxDemand = String(Math.round(r.md));
            onChange && onChange(index, patch);
            setUploadMsg(r.filled
                ? `${tr(lang, 'TNB bill read', '账单已读取')} — ${r.voltage || ''} · ${r.monthlyUsage ? Math.round(r.monthlyUsage).toLocaleString() : '?'} kWh · MD ${r.md || '?'} kW`
                : tr(lang, 'Could not read this bill — enter manually.', '无法读取，请手动输入。'));
        } catch (e) { setUploadMsg(tr(lang, 'Could not read this PDF — enter manually.', '无法读取此 PDF。')); }
    };
    const handleLoadProfileUpload = async (file) => {
        if (!file) return;
        setUploadMsg(tr(lang, 'Analysing load profile…', '分析用电概况…'));
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
            const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: true });
            const recs = parseTnbRows(rows);
            if (!recs.length) { setUploadMsg(tr(lang, 'No 30-min interval data found.', '未找到 30 分钟间隔数据。')); return; }
            const res = analyzeLoadProfile(recs, {});
            const rate = tariffRate;
            const patch = { ...meter, billKwh: String(Math.round(res.stats.monthlyKwh)), bill: _billFromKwh(res.stats.monthlyKwh, rate) };
            if (res.stats.mdKw) patch.maxDemand = String(Math.round(res.stats.mdKw));
            onChange && onChange(index, patch);
            onLpAnalysis && onLpAnalysis(index, res);   // lift the pattern to the parent for the combined chart
            setUploadMsg(`${tr(lang, 'Load profile', '用电概况')} — ${res.stats.nDays} ${tr(lang, 'days', '天')} · ~${Math.round(res.stats.monthlyKwh).toLocaleString()} kWh/mo · MD ${Math.round(res.stats.mdKw)} kW`);
        } catch (e) { setUploadMsg(tr(lang, 'Could not read this file.', '无法读取此文件。')); }
    };

    // Mode AUTO from Max Demand (VERBATIM from cscCombinedTab): MD entered = ATAP, blank = SELCo.
    const csMode = (parseFloat(maxDemand) || 0) > 0 ? 'atap' : 'selco';

    // Effective daytime usage % → off-hour baseline + full-self flag (VERBATIM).
    const _computedDayPct = (() => { const oh = Math.max(0, opEnd - opStart); if (oh <= 0) return 100; const ov = Math.max(0, Math.min(opEnd, 18) - Math.max(opStart, 8)); return Math.max(0, Math.min(100, Math.round(ov / oh * 100))); })();
    const _effDayPct = String(dayUsagePct).trim() !== '' ? Math.max(0, Math.min(100, parseFloat(dayUsagePct) || 0)) : _computedDayPct;
    const _offHour = Math.max(0, Math.min(1, (100 - _effDayPct) / 100));
    const _fullSelf = false;   // always use the bell-curve SELCo model (battery-aware) — user 2026-07-03

    const { atapPrices, batteryOptions } = useRscPricing('Residential Pricing', phaseMode, 999);
    // Data-driven LV battery NET price = Battery Pricing gross (16 kWh) − RM 800 promo. (2026-08-03)
    useEffect(() => {
        const gross = batteryOptions?.find(b => Number(b.kwhEach) === 16)?.cashEach;
        // See cscCombinedTab: the per-battery promo is RSC's constant, never a literal.
        if (gross > 0) setLvBattUnitNet(gross - PER_BATTERY_PROMO);
    }, [batteryOptions]);

    // Per-meter battery — matches cscCombinedTab (user 2026-07-10): NO auto-battery,
    // starts battery-free (''); the agent steps it in manually (16-kWh LV up to 96, then
    // HV combos). manualBatteryRef stays as the stepper's typed-value guard.
    // Seeded from the saved draft so a refresh keeps what the agent typed. (bug 2026-08-26)
    const [battery, setBattery] = React.useState(meter?.battery ?? '');
    // Battery brand per meter (user 2026-09-21): GoodWe = existing ladder; Huawei = 241-kWh blocks @ RM1,350/kWh.
    const [battBrand, setBattBrand] = React.useState(meter?.battBrand ?? BATT_BRAND_GOODWE);
    const isHuaweiBatt = battBrand === BATT_BRAND_HUAWEI;
    const manualBatteryRef = useRef(false);

    // Per-meter "Astern Discount" for ≤ 30 kWp (ATAP residential) meters — mirrors the
    // single-meter calculator: flat RM2,200 (RSC with-backupbox rate). Checkbox per meter.
    // (user 2026-08-07)
    const [asternOn, setAsternOn] = React.useState(!!meter?.asternOn);
    const asternDiscForMeter = () => {
        const k = Number(sysFor()?.kwp) || 0;
        return (k > 0 && k <= 30) ? 2200 : 0;
    };

    // ── Per-meter SYSTEM OVERRIDES (mirrors cscCombinedTab.cscSys[key]) ──
    // Blank/undefined = follow the auto sizing (sysFor falls back to optB). A typed
    // value sticks. Keys mirror cscCombinedTab: panels, kwp(derived), panelWatt,
    // inverterKw, brand, type, systemCost, pricePerKwp, addOn.
    const [sys, setSys] = React.useState(meter?.sys ?? {});
    const setSysField = (field, value) => setSys(prev => ({ ...prev, [field]: value }));

    // ── Per-meter catalogue add-on rows (mirrors cscCombinedTab's CardAddons) ──
    // `addonCatalog` = shared catalog; `addonRows` = this meter's picked rows.
    // Folds into cardPricing alongside the flat Add-on (RM) field.
    const [addonCatalog, setAddonCatalog] = React.useState([]);
    const [addonRows, setAddonRows] = React.useState(meter?.addonRows ?? []);

    // Mirror this row's OWN inputs up into the parent meter object, so the tab's
    // sessionStorage draft can restore them.
    //
    // These four lived only in the row's local state, while the draft saved `meters`
    // (bill / maxDemand / tariff). A refresh therefore restored the bill but silently
    // reset the system back to auto sizing — the agent's kWp, battery, Astern discount
    // and catalogue add-ons all vanished, and the quote quietly changed underneath them.
    // (bug 2026-08-26)
    //
    // Sent as a PARTIAL patch (changeMeter merges), so this can never clobber a bill or
    // tariff edit made in the same tick. The signature guard stops the write→re-render→
    // write loop that a naive effect here would cause.
    // HYDRATE — React runs child effects BEFORE parent effects, so on a page load this
    // row mounts against the EMPTY initial meter; the parent's sessionStorage restore
    // (setMeters) only lands afterwards. useState reads its initial value once and never
    // again, so without this the restored draft arrives as a prop and is ignored, leaving
    // the row showing auto-sizing while the draft held the agent's real system.
    // Set by the draft restore so the re-size effect below skips one pass.
    const skipResizeRef = useRef(false);
    const hydratedRef = useRef(false);
    useEffect(() => {
        if (hydratedRef.current) return;
        const hasSaved = meter && (meter.sys || meter.battery || meter.asternOn
                                   || (meter.addonRows && meter.addonRows.length));
        if (!hasSaved) return;
        hydratedRef.current = true;
        setBattery(meter.battery ?? '');
        setBattBrand(meter.battBrand ?? BATT_BRAND_GOODWE);
        setAsternOn(!!meter.asternOn);
        setSys(meter.sys ?? {});
        setAddonRows(meter.addonRows ?? []);
        if (meter.battery) manualBatteryRef.current = true;   // typed value, not auto
        // The restore also lands the saved BILL, which the re-size effect below reads as
        // "the agent changed the bill" and answers by stripping the manual panel /
        // inverter / cost overrides we just put back. Tell it to sit out that one pass.
        skipResizeRef.current = true;
    }, [meter]);

    // MIRROR — push this row's own inputs back into the parent meter so the draft saves
    // them. The first run is skipped deliberately: it would otherwise write the empty
    // mount-time state over the draft before the restore above has had a chance to run.
    const mirrorSigRef = useRef(null);
    useEffect(() => {
        const patch = { battery, battBrand, asternOn, sys, addonRows };
        const sig = JSON.stringify(patch);
        if (mirrorSigRef.current === null) { mirrorSigRef.current = sig; return; }
        if (sig === mirrorSigRef.current) return;
        mirrorSigRef.current = sig;
        onChange && onChange(index, patch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [battery, battBrand, asternOn, sys, addonRows, index]);
    useEffect(() => {
        let c = false;
        getAddons().then(r => { if (!c) setAddonCatalog(r.data || []); }).catch(() => {});
        return () => { c = true; };
    }, []);
    const addAddonRow = (addonId) => {
        const cat = (addonCatalog || []).find(a => a.addonId === addonId);
        if (!cat) return;
        const rowId = `r-${addonId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        // Same rule as the Combined tab twin: PerKwp opens at this meter's kWp.
        const _sysKwp = Number(sysFor()?.kwp) || 0;
        setAddonRows(prev => [...prev, { ...cat, rowId, _qty: defaultQtyFor(cat.unitType, { kwp: _sysKwp }) }]);
    };
    const updateAddonRow = (rowId, patch) => setAddonRows(prev => prev.map(r => r.rowId === rowId ? { ...r, ...patch } : r));
    const removeAddonRow = (rowId) => setAddonRows(prev => prev.filter(r => r.rowId !== rowId));
    // Residential band: surface the inverter auto-upgrade as ONE read-only "Inverter Upgrade (NkW)"
    // add-on row (final kW, chained catalog cost) — shows in the add-on list + "+ Add-ons"
    // line. Computed, not stored. (user 2026-07-04)
    const autoInvUpgRows = () => {
        const s = sysFor();
        const kwpN = Number(s.kwp) || 0;
        // ≤ 30 kWp = the ATAP-residential band — same boundary as the pricing. (was <26,
        // stale after the boundary moved to 30 on 2026-10-14)
        if (!(kwpN > 0 && kwpN <= 30)) return [];
        const rows = [];
        const upg = commercialInverterUpgrade(kwpN, Number(s.panels) || 0, { phaseMode, addonCatalog, rows: atapPrices });
        if (upg.cost > 0) rows.push({ itemName: `Inverter Upgrade (${upg.kw}kW)`, price: String(upg.cost), rowId: 'auto-inv', _qty: 1, _autoInvUpg: true });
        // Residential-band brand STRING adjustment (Solis −2000, Huawei −500) — under-inverter
        // row, adds to total as a negative, not an add-on-column row. (user 2026-08-04)
        const _adj = BRAND_STRING_ADJ_SMALL[s.brand];
        if (_adj != null) rows.push({ itemName: `${s.brand === 'huawei' ? 'Huawei' : 'Solis'} Inverter`, price: String(_adj), rowId: 'auto-brand', _qty: 1, _autoInvUpg: true });
        return rows;
    };
    const effAddonRows = () => (addonRows || []).concat(autoInvUpgRows());
    // Re-size on any sizing-input change: drop the manual panel / inverter / cost
    // overrides + manual battery so a new bill (or operating-days / hours / daytime /
    // MD / tariff) RE-PROPOSES the system, instead of sticking at the edited values.
    // Brand / panel-watt / per-kWp / add-on are kept. VERBATIM intent from
    // cscCombinedTab's _firstSizeRef effect (line ~883).
    const _firstSizeRef = useRef(true);
    useEffect(() => {
        if (_firstSizeRef.current) { _firstSizeRef.current = false; return; }
        // A draft restore arrives as a bill change, but the agent did not touch anything —
        // re-proposing here would throw away the very overrides just restored. Skip exactly
        // one pass; a real edit afterwards still re-sizes as normal. (bug 2026-08-26)
        if (skipResizeRef.current) { skipResizeRef.current = false; return; }
        setSys(prev => { const { panels, inverterKw, systemCost, kwp, ...rest } = prev; return rest; });
        manualBatteryRef.current = false;   // let the auto-battery default re-pick
        // Small meters (≤ RM1,500) suggest 1 battery unit (16 kWh) — mirrors the
        // single-meter Calculator (cscCombinedTab). Larger meters stay battery-manual.
        // (user 2026-07-22)
        const _b = parseFloat(bill) || 0;
        setBattery(_b > 0 && _b <= 1500 ? '16' : '');
        setBattBrand(BATT_BRAND_GOODWE);   // the 16-kWh default is a GoodWe unit
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bill, meter.mvPeakKwh, meter.mvOffKwh, maxDemand, tariffRate, operatingDays, opStart, opEnd, dayUsagePct, phaseMode]);

    // Size EXACTLY like cscCombinedTab's optA (the no-battery sizing): pass the
    // Option-A battKwhTotal=0 / invType='string' to the SIZING hook so the candidate
    // SELECTION (kWp / panels / inverter) is identical to the single-meter Calculator.
    // Option B = that same system + battery; the battery's hybrid REPRICE happens
    // inline in sysFor() below (mirrors setCardBattery), so the final price matches too.
    const { candidates, snap96 } = useCscBillProposal({
        residentialRows: atapPrices,
        monthlyBill: parseFloat(calcBill) || 0,
        tariffRate: calcRate, operatingDays, opStart, opEnd,
        maxDemandKw: parseFloat(maxDemand) || 0,
        // Per-meter brand drives this meter's own sizing (falls back to the shared default);
        // each meter picks its brand independently in System Details, like the Calculator.
        phaseMode, mode: csMode, brand: sys.brand || brand,
        invType: 'string',
        battKwhTotal: 0,
        offHourBaselinePct: _offHour,
        fullSelfUse: _fullSelf,
    });

    // Correct SYSTEM cash price for a panel count: ≤30 kWp → raw ATAP residential list
    // (user: "<26kWp must follow ATAP", raised to 30 on 2026-10-14); >30 kWp → commercial tier. Mirrors the hook's
    // buildCandidate pricing; used by the editable System Details so a panel edit re-prices
    // on the right basis. Returns 0 → caller keeps the prior price.
    const priceForPanels = (panels, kwp, b = 'goodwe', type = 'string') => {
        const n = Number(panels) || 0;
        if (n <= 0 || !(kwp > 0)) return 0;
        let residentialCash = 0;
        if (kwp <= 30 && Array.isArray(atapPrices)) {
            const row = atapPrices.find(r => Number(r.panels) === n);
            if (row) residentialCash = phaseMode === '3phase' ? row.cash3p : row.cash;
        }
        return getCommercialPrice({ kwp, brand: b, type, residentialCash, is3p: phaseMode === '3phase' });
    };

    // ── Option B sizing — pickPct / fillInverter / snapNear96 / billTargetCard now
    // live in ../../utils/cscSizing (shared with cscCombinedTab.jsx — see import above).
    // `sizingCtx` bundles the closed-over values they need. ──
    // SELCo-only daytime downsizing (shared helper) — per-meter sizing is battery-free
    // (battKwhTotal:0 above), so hasBattery=false here. (user 2026-08-02)
    const sizingCtx = { candidates, bill: calcBill, tariffRate: calcRate, mode: csMode, daytimeFrac: 1 - _offHour, hasBattery: false };
    // Option A's system (no-battery sizing); Option B = the SAME system + battery.
    const optA = snapNear96(billTargetCard(sizingCtx), 'p50', snap96);
    const optB = optA;

    // No auto-battery (user 2026-07-10): battery is stepper-only, never suggested by default.

    // ── Battery stepper handler (mirror cscCombinedTab.setCardBattery) ── Stepper commits a
    // valid ladder value (no snap). Battery present → set inverter type by product: LV (<100 kWh)
    // → GoodWe Hybrid; HV (≥100 kWh) → String (AC-coupled). Reprice for the resulting type.
    const onBatteryBlur = (v, brandForBatt = battBrand) => {
        manualBatteryRef.current = true;
        setBattery(v);
        const n = parseFloat(v) || 0;
        if (n > 0) {
            const k = Number(sysFor()?.kwp) || 0;
            // A Huawei battery is an HV, AC-coupled product → String.
            const type = (brandForBatt === BATT_BRAND_HUAWEI || isHvBattery(n)) ? 'string' : 'hybrid';
            const brand = type === 'hybrid' ? 'goodwe' : (sysFor()?.brand || 'goodwe');
            const ppk = commercialPricePerKwp(k, brand, type, 0);
            if (ppk > 0 && k > 0) setSys(prev => ({ ...prev, brand, type, pricePerKwp: String(ppk), systemCost: String(Math.round(ppk * k)) }));
        }
    };

    // ── Per-meter system snapshot — honours the per-meter OVERRIDES (`sys`), falling
    // back to the auto Option-B sizing (optB) when an override is blank. This is the
    // EXACT mirror of cscCombinedTab.sysFor(key, card): panels = source of truth, kWp
    // ALWAYS derived = panels × (watt/1000), battery forces GoodWe Hybrid pricing. ──
    const sysFor = () => {
        const o = sys || {};
        const card = optB;
        const panelWatt = o.panelWatt != null ? o.panelWatt : '650';
        const wpk = (Number(panelWatt) || 650) / 1000;
        const panelsNum = o.panels != null ? Number(o.panels)
            : (card?.kwp != null ? Math.ceil(Number(card.kwp) / wpk) : (card?.panels != null ? Number(card.panels) : 0));
        const panels = panelsNum ? String(panelsNum) : '';
        const kwp = panelsNum ? String(+(panelsNum * wpk).toFixed(2)) : (card?.kwp != null ? String(card.kwp) : '');
        let inverterKw = o.inverterKw != null ? o.inverterKw : (card?.inverterKw != null ? String(card.inverterKw) : '');
        // Brand↔type link: GoodWe does BOTH String and Hybrid (user 2026-08-19);
        // Solis/Huawei stay String-only. Everything defaults to GoodWe now.
        let mBrand = o.brand != null ? o.brand : 'goodwe';
        let type = o.type != null ? o.type : (mBrand === 'goodwe' ? 'hybrid' : 'string');
        if (Number(battery) > 0) {
            if (isHuaweiBatt) type = 'string';            // Huawei HV battery: String on the chosen brand
            // GoodWe LV (<100 kWh) is DC-coupled Hybrid; HV (>=100 kWh) is AC-coupled String.
            else { mBrand = 'goodwe'; type = isHvBattery(battery) ? 'string' : 'hybrid'; }
        }
        if (mBrand === 'huawei' || mBrand === 'solis') type = 'string';
        // The residential band stays Hybrid-only for GoodWe — that band prices off the residential
        // row and carries the Solis/Huawei small-system adjustment.
        if (mBrand === 'goodwe' && (Number(kwp) || 0) <= 30) type = 'hybrid';
        // A >60 kW inverter can't be Hybrid (60 kW hybrid stack ceiling) → String, AC-coupled.
        if (exceedsHybridMax(Number(kwp) || 0, RATIO_CAP_NO_BATT)) type = 'string';
        // GoodWe String is valid as of 2026-08-19 — only the residential band still falls back.
        if (type === 'string' && mBrand === 'goodwe' && (Number(kwp) || 0) <= 30) mBrand = 'solis';
        let systemCost = o.systemCost != null ? o.systemCost : (card?.cashPrice != null ? String(Math.round(card.cashPrice)) : '');
        let pricePerKwp = o.pricePerKwp != null ? o.pricePerKwp : ((card?.cashPrice && card?.kwp) ? String(Math.round(card.cashPrice / card.kwp)) : '');
        // Auto price follows THIS METER's brand / type, not the tab-level brand the sizing hook
        // priced with — otherwise a Huawei meter is quoted at the GoodWe rate and PER KWP
        // disagrees with PANEL TOTAL. Same fix as the single-meter card. (user 2026-09-25)
        if (o.systemCost == null && Number(battery) <= 0) {
            const _k = Number(kwp) || 0;
            const _ppk = _k > 30 ? commercialPricePerKwp(_k, mBrand, type, 0) : 0;
            if (_ppk > 0) { pricePerKwp = String(_ppk); systemCost = String(Math.round(_ppk * _k)); }
        }
        // Battery present + NO manual systemCost override → reprice at GoodWe Hybrid tier
        // (matches cscCombinedTab.setCardBattery). A user-typed systemCost wins.
        if (Number(battery) > 0 && o.systemCost == null) {
            const k = Number(kwp) || 0;
            const ppk = isHuaweiBatt ? commercialPricePerKwp(k, mBrand, 'string', 0) : commercialPricePerKwp(k, 'goodwe', 'hybrid', 0);
            if (ppk > 0 && k > 0) { pricePerKwp = String(ppk); systemCost = String(Math.round(ppk * k)); }
        }
        // Residential band: bump the ATAP inverter to clear the 1.35 commercial ratio — inverter field
        // shows the final kW; systemCost stays RAW. The upgrade cost is an auto add-on row
        // (see autoInvUpgRows) shown in the add-on list + "+ Add-ons" line. (user 2026-07-04)
        const _kwpNum = Number(kwp) || 0;
        if (_kwpNum > 0 && _kwpNum <= 30) {
            inverterKw = String(commercialInverterUpgrade(_kwpNum, panelsNum, { phaseMode, addonCatalog, rows: atapPrices }).kw);
        }
        const addOn = o.addOn != null ? o.addOn : '';
        const om = String(omForKwp(kwp, 'premium'));
        return { kwp, panels, panelWatt, inverterKw, brand: mBrand, type, systemCost, pricePerKwp, addOn, om };
    };

    // ── Pricing (VERBATIM from cscCombinedTab.cardPricing; flat add-on now supported,
    // no per-card discount in multi-meter) ──
    const cardPricing = () => {
        const s = sysFor();
        const panel = Number(s.systemCost) || 0;   // raw ATAP; inverter upgrade is an add-on row
        const batteryCost = batteryCostFor(battery, battBrand);
        // add-ons = flat per-meter add-on + Σ catalogue rows (incl auto inverter-upgrade row)
        const addons = (Number(s.addOn) || 0) + addonRowsTotal(effAddonRows());
        // Residential-band Astern discount (RM2,200) when ticked — reduces the panel (system) price only.
        const discAmt = asternOn ? Math.min(panel, asternDiscForMeter()) : 0;
        const panelNet = Math.max(0, panel - discAmt);
        const cash = panelNet + batteryCost + addons;
        // Instalments finance what the customer owes, INCLUDING SST (user 2026-08-21).
        // Matches the single-meter Combined tab; ccFromCash carries the shared CC_FACTORS
        // and the roundUp10 rule so CSC no longer keeps its own copy of the rates.
        const ccBase = cash + sstOn(cash);
        const cc24 = ccFromCash(ccBase, 24);
        const cc = ccFromCash(ccBase, 36);
        const cc60 = ccFromCash(ccBase, 60);
        return { panel, panelNet, battery: batteryCost, addons, discAmt, cash, cc24, cc, cc60 };
    };

    // ── Saving / ROI (VERBATIM from cscCombinedTab.optBDisplay) ──
    // ATAP (export) only when MD is keyed AND the inverter kW fits within MD; SELCo
    // (zero export) when MD is blank OR the inverter kW exceeds MD. (user 2026-07-24)
    const _mdRow = parseFloat(maxDemand) || 0;
    const _effKwpRow = Number(sysFor()?.kwp) || Number(optB?.kwp) || 0;
    const _neededInvRow = _effKwpRow > 0 ? inverterFromKwp(_effKwpRow, RATIO_CAP_NO_BATT, sysFor()?.type || 'string', sysFor()?.brand) : 0;
    const _csMode = (csMode === 'selco' || (_mdRow > 0 && _neededInvRow > _mdRow)) ? 'selco' : 'atap';
    const optBDisplay = (() => {
        if (!optB) return null;
        const monthlyKwh = calcRate > 0 ? (parseFloat(calcBill) || 0) / calcRate : 0;
        const battB = parseFloat(battery) || 0;
        const kwpB = Number(sysFor()?.kwp) || Number(optB?.kwp) || 0;
        const recomputeB = (kwp) => {
            let e = _csMode === 'selco'
                ? estimateMonthlySavingSELCo({ kwp, tariffRate: calcRate, monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal: battB, offHourBaselinePct: _offHour })
                : estimateMonthlySaving({ kwp, tariffRate: calcRate, monthlyKwh, operatingDays, opStart, opEnd, battKwhTotal: battB, offHourBaselinePct: _offHour });
            if (_fullSelf) {
                const selfFull = Math.min(e.generated * Math.min(1, (operatingDays || 7) / 7), monthlyKwh);
                e = { ...e, selfConsumed: selfFull, exported: 0, importAfterSolar: Math.max(0, monthlyKwh - selfFull), monthlySaving: selfFull * calcRate };
            }
            return e;
        };
        let energyB = { ...optB, kwp: kwpB, mode: _csMode, ...recomputeB(kwpB) };
        const consB = Math.round((energyB?.selfConsumed || 0) + (energyB?.importAfterSolar || 0));
        const origB = billFn(consB);
        const newB = billFn(Math.round(energyB?.importAfterSolar || 0));
        const credB = (energyB.mode !== 'selco') ? Math.round(energyB.exported || 0) * 0.20 : 0;
        const savingMoB = Math.max(0, Math.round((origB.total || 0) - Math.max(0, (newB.total || 0) - credB)));
        const capitalB = cardPricing().cash;
        const roiB = (savingMoB * 12) > 0 ? Math.round((capitalB / (savingMoB * 12)) * 100) / 100 : null;
        const savingPctB = (parseFloat(bill) > 0 && savingMoB) ? savingMoB / parseFloat(bill) : (energyB?.savingPct ?? null);
        return { ...energyB, monthlySaving: savingMoB, roiYears: roiB, savingPct: savingPctB };
    })();

    // ── Report the result up so the parent can combine ──
    const s = sysFor();
    // Changing the inverter auto-refills the panels to the MOST that inverter can carry at
    // the 1.42 ratio — inverter & panels lock-step so the ratio can never exceed 1.42.
    // > 40 kW can't be hybrid → flip type to String. Mirrors cscCombinedTab. (user 2026-07-22)
    const applyInverterKw = (newInv) => {
        if (!(Number(newInv) > 0)) return;
        const wpk = (Number(s.panelWatt) || 650) / 1000;
        let t = s.type || 'string';
        if (newInv > 40 && t === 'hybrid' && !isHvBattery(battery)) { t = 'string'; setSysField('type', 'string'); }
        const newPanels = fillPanelsForInverter(newInv, wpk, RATIO_CAP_NO_BATT);
        const newKwp = +(newPanels * wpk).toFixed(2);
        setSysField('inverterKw', String(newInv));
        setSysField('panels', String(newPanels));
        const _price = priceForPanels(newPanels, newKwp, s.brand || 'goodwe', t);
        if (_price > 0) { setSysField('systemCost', String(Math.round(_price))); setSysField('pricePerKwp', String(Math.round(_price / newKwp))); }
        else if (s.pricePerKwp) { setSysField('systemCost', String(Math.round(Number(s.pricePerKwp) * newKwp))); }
    };
    const P = cardPricing();
    // Catalogue add-on rows shaped as create-sale line items (mirror cscCombinedTab).
    // The flat Add-on (RM) field stays folded into `addons` and is emitted as its own
    // line by the parent; these are the per-catalogue-item lines.
    const flatAddOn = Number(s.addOn) || 0;
    const addonLineItems = effAddonRows().map((r) => {
        const qtyBearing = isQtyBearing(r.unitType);
        const qty = qtyBearing ? (Number(r._qty) || 1) : 1;
        const total = computeAddonAmount(r, qty).total;
        return {
            ItemName: r.itemName || r.name || 'Add-on',
            Category: r.category || null,
            IsBattery: false,
            Qty: qty,
            UnitPrice: qty > 0 ? Math.round((total / qty) * 100) / 100 : total,
            LineTotal: total,
        };
    }).filter(it => it.LineTotal > 0);
    const result = optB ? {
        index,
        kwp: parseFloat(s.kwp) || 0,
        panels: parseFloat(s.panels) || 0,
        panelWatt: Number(s.panelWatt) || 650,
        inverterKw: parseFloat(s.inverterKw) || 0,
        brand: s.brand,
        invType: s.type,           // 'hybrid' | 'string' — the proposal prints this, not a guess
        battKwh: parseFloat(battery) || 0,
        battBrand,
        om: parseFloat(s.om) || 0,
        mode: _csMode,
        bill: parseFloat(bill) || 0,
        tariffRate: calcRate,
        tariffKey: tariffRate,     // the tariff OPTION (0.303 = MV) — tariffRate is the calc rate
        maxDemand: parseFloat(maxDemand) || 0,
        // pricing
        panel: P.panel, panelNet: P.panelNet, batteryCost: P.battery, addons: P.addons, discAmt: P.discAmt,
        flatAddOn,                 // flat per-meter Add-on (RM) field
        addonLineItems,            // catalogue add-on rows as line items
        cash: P.cash, cc24: P.cc24, cc36: P.cc, cc60: P.cc60,
        // saving / roi
        savingMo: optBDisplay?.monthlySaving || 0,
        roiYears: optBDisplay?.roiYears ?? null,
        savingPct: optBDisplay?.savingPct ?? null,
        // energy breakdown — summed into the combined chart + breakdown (multi-meter parity)
        monthlyKwh: calcRate > 0 ? Math.round((parseFloat(calcBill) || 0) / calcRate) : 0,
        generated: Math.round(optBDisplay?.generated || 0),
        selfConsumed: Math.round(optBDisplay?.selfConsumed || 0),
        exported: Math.round(optBDisplay?.exported || 0),
        curtailed: Math.round(optBDisplay?.curtailed || 0),
        importAfterSolar: Math.round(optBDisplay?.importAfterSolar || 0),
        battCharge: Math.round(optBDisplay?.battMonthlyChargeKwh || 0),
        battUsed: Math.round(optBDisplay?.battMonthlySelfKwh || 0),
        exportCredit: (optBDisplay && optBDisplay.mode !== 'selco') ? Math.round(optBDisplay.exported || 0) * 0.20 : 0,
        selfUseSaving: Math.max(0, (optBDisplay?.monthlySaving || 0) - ((optBDisplay && optBDisplay.mode !== 'selco') ? Math.round(optBDisplay.exported || 0) * 0.20 : 0)),
    } : null;

    // Push the result upward whenever it materially changes. Stringify-guarded so
    // we don't loop (React state up in the parent triggers re-render → re-run here).
    const lastSigRef = useRef('');
    useEffect(() => {
        const sig = JSON.stringify(result);
        if (sig !== lastSigRef.current) {
            lastSigRef.current = sig;
            onResult && onResult(index, result);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(result), index]);

    const setField = (field, value) => onChange && onChange(index, { ...meter, [field]: value });

    return (
        <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            {/* Meter header bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, bgcolor: '#F1F5F9', borderBottom: `1px solid ${COLORS.border}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                    {/* A real, bordered input rather than text that only reveals itself on
                        click. The borderless version was invisible as a control — nobody
                        knew the name could be changed, which is the same as not having it.
                        (user 2026-10-14: "instead of typeable can we have input field
                        easier for them to see?")
                        Empty falls back to the lettered default everywhere it is printed. */}
                    <TextField
                        size="small"
                        value={meter.label ?? ''}
                        onChange={(e) => onChange && onChange(index, { ...meter, label: e.target.value })}
                        placeholder={tr(lang, `Meter ${meterLetter(index)}`, `电表 ${meterLetter(index)}`)}
                        label={tr(lang, 'Meter name', '电表名称')}
                        InputLabelProps={{ shrink: true, sx: { fontFamily: PP, fontSize: '0.78rem' } }}
                        inputProps={{ maxLength: 40 }}
                        sx={{
                            minWidth: 0, width: { xs: 160, sm: 230 },
                            bgcolor: '#fff',
                            '& .MuiInputBase-input': {
                                fontWeight: 700, fontSize: '0.82rem', color: COLORS.text, fontFamily: PP,
                                py: 0.6,
                            },
                            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                        }} />
                    {optB && <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.primary, fontFamily: PP, whiteSpace: 'nowrap' }}>· {s.kwp} kWp · {_csMode === 'selco' ? 'SELCo' : 'ATAP'}</Typography>}
                </Box>
                {count > 1 && (
                    <Button size="small" onClick={() => onRemove && onRemove(index)}
                        sx={{ minWidth: 0, px: 1, py: 0.2, fontSize: '0.72rem', fontFamily: PP, fontWeight: 700, textTransform: 'none', color: '#EF4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}>
                        ✕ {tr(lang, 'Remove', '移除')}
                    </Button>
                )}
            </Box>

            {/* Per-meter inputs: Bill · Usage(kWh) · Max Demand · Tariff */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1.2fr' }, gap: 1.5, p: 1.5, alignItems: 'end' }}>
                {isMv ? (
                    <>
                        {/* Medium Voltage: peak / off-peak kWh + Max Demand; the bill is calculated. */}
                        <Box>
                            <Label>{tr(lang, 'Peak Usage (kWh)', '峰时用电 (kWh)')}</Label>
                            <TextField fullWidth size="small" type="text" inputMode="numeric"
                                name={`mm-peak-${index}`} autoComplete="off"
                                value={meter.mvPeakKwh ? Number(String(meter.mvPeakKwh).replace(/,/g, '')).toLocaleString('en-US') : ''}
                                onChange={(e) => onChange && onChange(index, { ...meter, mvPeakKwh: e.target.value.replace(/[^\d.]/g, '') })}
                                placeholder="e.g. 86,258"
                                inputProps={{ autoComplete: 'off' }}
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.88rem', borderRadius: '8px' } }} />
                        </Box>
                        <Box>
                            <Label>{tr(lang, 'Off-Peak Usage (kWh)', '非峰时用电 (kWh)')}</Label>
                            <TextField fullWidth size="small" type="text" inputMode="numeric"
                                name={`mm-offpeak-${index}`} autoComplete="off"
                                value={meter.mvOffKwh ? Number(String(meter.mvOffKwh).replace(/,/g, '')).toLocaleString('en-US') : ''}
                                onChange={(e) => onChange && onChange(index, { ...meter, mvOffKwh: e.target.value.replace(/[^\d.]/g, '') })}
                                placeholder="e.g. 118,565"
                                helperText={Number(bill) > 0 ? `${tr(lang, 'Bill', '账单')} RM ${Number(bill).toLocaleString('en-US')}${mvCtx.mdKw > 0 ? '' : tr(lang, ' · enter MD', ' · 请填最高需求')}` : ''}
                                inputProps={{ autoComplete: 'off' }}
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.88rem', borderRadius: '8px' } }} />
                        </Box>
                    </>
                ) : (
                    <>
                        <Box>
                            <Label>{tr(lang, 'Monthly Bill (RM)', '月账单 (RM)')}</Label>
                            <TextField fullWidth size="small" type="text" inputMode="numeric"
                                name={`mm-bill-${index}`} autoComplete="off"
                                value={bill === '' || bill == null ? '' : Number(String(bill).replace(/,/g, '')).toLocaleString('en-US')}
                                onChange={(e) => { const v = e.target.value.replace(/[^\d.]/g, ''); onChange && onChange(index, { ...meter, bill: v, billKwh: _kwhFromBill(v, tariffRate) }); }}
                                placeholder="e.g. 8,500"
                                inputProps={{ autoComplete: 'off' }}
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.88rem', borderRadius: '8px' } }} />
                        </Box>
                        <Box>
                            <Label>{tr(lang, 'Monthly Usage (kWh)', '月用电 (kWh)')}</Label>
                            <TextField fullWidth size="small" type="text" inputMode="numeric"
                                name={`mm-usage-${index}`} autoComplete="off"
                                value={billKwh === '' || billKwh == null ? '' : Number(String(billKwh).replace(/,/g, '')).toLocaleString('en-US')}
                                onChange={(e) => { const v = e.target.value.replace(/[^\d.]/g, ''); onChange && onChange(index, { ...meter, billKwh: v, bill: _billFromKwh(v, tariffRate) }); }}
                                placeholder="e.g. 16,500"
                                inputProps={{ autoComplete: 'off' }}
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.88rem', borderRadius: '8px' } }} />
                        </Box>
                    </>
                )}
                <Box>
                    <Label>{tr(lang, 'Max Demand (kW)', '最高需求 (kW)')}</Label>
                    <TextField fullWidth size="small" type="number" value={maxDemand}
                        name={`mm-md-${index}`} autoComplete="off"
                        onChange={(e) => setField('maxDemand', e.target.value)}
                        placeholder={isMv ? tr(lang, 'required (kW P)', '必填 (kW P)') : tr(lang, 'blank = SELCo', '空白 = SELCo')}
                        inputProps={{ autoComplete: 'off' }}
                        InputProps={{ sx: { fontFamily: PP, fontSize: '0.88rem', borderRadius: '8px' } }} />
                </Box>
                <Box>
                    <Label>{tr(lang, 'Tariff', '电费')}</Label>
                    <Select fullWidth size="small" value={tariffRate} onChange={(e) => { const r = Number(e.target.value); onChange && onChange(index, { ...meter, tariffRate: r, billKwh: _kwhFromBill(bill, r) }); }}
                        sx={{ fontFamily: PP, fontSize: '0.82rem', borderRadius: '8px' }}>
                        {TARIFFS.map(t => (
                            <MenuItem key={t.rate} value={t.rate} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>{isMvTariff(t.rate) ? t.label : `${t.label} — RM ${t.rate}`}</MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>

            {/* Import (optional) — auto-fill this meter from a TNB e-bill (PDF) or a 30-min
                load-profile CSV/Excel. If one meter has a profile, others adapt its pattern. */}
            <Box sx={{ px: 1.5, pb: 1.5 }}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: COLORS.sub, textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: PP, mb: 0.5 }}>{tr(lang, 'Import (optional)', '导入(可选)')}</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button component="label" size="small" variant="outlined" sx={{ fontFamily: PP, fontSize: '0.72rem', textTransform: 'none', borderRadius: '8px' }}>
                        {tr(lang, 'TNB bill (PDF)', 'TNB 账单 (PDF)')}
                        <input hidden type="file" accept="application/pdf,.pdf" onChange={(e) => { handleTnbUpload(e.target.files?.[0]); e.target.value = ''; }} />
                    </Button>
                    <Button component="label" size="small" variant="outlined" sx={{ fontFamily: PP, fontSize: '0.72rem', textTransform: 'none', borderRadius: '8px' }}>
                        {tr(lang, 'Load profile (CSV/Excel)', '用电概况 (CSV/Excel)')}
                        <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { handleLoadProfileUpload(e.target.files?.[0]); e.target.value = ''; }} />
                    </Button>
                </Box>
                {uploadMsg && <Typography sx={{ fontSize: '0.66rem', color: '#0F766E', fontFamily: PP, mt: 0.5 }}>{uploadMsg}</Typography>}
            </Box>

            {/* Editable System Details — each meter owns its FULL editable spec, exactly
                like cscCombinedTab's OptionCard. Defaults to the auto Option-B sizing;
                edits stick. Blank field = auto. kWp / panels / cost interlock the same way
                (kWp → panels → systemCost; per-kWp ↔ panel-total). */}
            {optB && (parseFloat(bill) > 0) && (
                <Box sx={{ mx: 1.5, mb: 1.5, p: 1.25, borderRadius: 2, bgcolor: '#F8FAFC', border: `1px solid ${COLORS.border}` }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 0.75 }}>
                        {tr(lang, 'System Details', '系统配置')} <Typography component="span" sx={{ fontWeight: 400, color: COLORS.sub, fontSize: '0.62rem' }}>· {tr(lang, 'blank = auto', '空白 = 自动')}</Typography>
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1 }}>
                        <Box>
                            <MiniLabel>{tr(lang, 'System kWp', '系统 kWp')}</MiniLabel>
                            <TextField size="small" type="number" fullWidth value={s.kwp ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    const wpk = (Number(s.panelWatt) || 650) / 1000;
                                    const p = v ? Math.ceil(Number(v) / wpk) : 0;
                                    setSysField('panels', p ? String(p) : '');
                                    // Price + inverter follow the edited kWp (user 2026-06-29).
                                    const _newKwp = (p || 0) * wpk;
                                    if (_newKwp > 0) {
                                        const _price = priceForPanels(p, _newKwp, s.brand || 'goodwe', s.type || 'string');
                                        if (_price > 0) {
                                            setSysField('systemCost', String(Math.round(_price)));
                                            setSysField('pricePerKwp', String(Math.round(_price / _newKwp)));
                                        } else if (s.pricePerKwp) {
                                            setSysField('systemCost', String(Math.round(Number(s.pricePerKwp) * _newKwp)));
                                        }
                                        setSysField('inverterKw', String(inverterFromKwp(_newKwp, RATIO_CAP_NO_BATT, s.type || 'string', s.brand)));
                                    }
                                }}
                                inputProps={{ min: 0, step: 0.01 }} sx={MINI_INPUT_SX} />
                        </Box>
                        <Box>
                            <MiniLabel>{tr(lang, 'Panels', '电板')}</MiniLabel>
                            <TextField size="small" type="number" fullWidth value={s.panels ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    const wpk = (Number(s.panelWatt) || 650) / 1000;
                                    setSysField('panels', v);
                                    // Price + inverter follow the panel count (user 2026-06-29).
                                    const _newKwp = (Number(v) || 0) * wpk;
                                    if (_newKwp > 0) {
                                        const _price = priceForPanels(Number(v), _newKwp, s.brand || 'goodwe', s.type || 'string');
                                        if (_price > 0) {
                                            setSysField('systemCost', String(Math.round(_price)));
                                            setSysField('pricePerKwp', String(Math.round(_price / _newKwp)));
                                        } else if (s.pricePerKwp) {
                                            setSysField('systemCost', String(Math.round(Number(s.pricePerKwp) * _newKwp)));
                                        }
                                        setSysField('inverterKw', String(inverterFromKwp(_newKwp, RATIO_CAP_NO_BATT, s.type || 'string', s.brand)));
                                    }
                                }}
                                inputProps={{ min: 0, step: 1 }} sx={MINI_INPUT_SX} />
                        </Box>
                        <Box>
                            <MiniLabel>{tr(lang, 'Inverter kW', '逆变器 kW')}</MiniLabel>
                            {/* Stepper (same design as Battery): REAL inverter models only;
                                panels auto-refill so ratio ≤ 1.42. No free-text. (user 2026-07-22) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                                {[['−', inverterModelAtMost, -0.01], ['+', inverterModelAtLeast, 0.01]].map(([sym, fn, d], i) => (
                                    <Button key={i} size="small" variant="outlined"
                                        onClick={() => { const t = s.type || 'string'; const cur = Number(s.inverterKw) || 0; applyInverterKw(fn(cur + d, t)); }}
                                        sx={{ minWidth: 24, width: 24, height: 30, p: 0, fontSize: '0.95rem', lineHeight: 1, order: i === 0 ? 0 : 2 }}>{sym}</Button>
                                ))}
                                <Box sx={{ flex: 1, order: 1, textAlign: 'center', fontFamily: PP, fontSize: '0.66rem', fontWeight: 700 }}>
                                    {Number(s.inverterKw) > 0 ? `${s.inverterKw} kW` : '—'}
                                </Box>
                            </Box>
                        </Box>
                        <Box>
                            {/* Inverter Type — Hybrid greyed out >40kW / when a HV battery forces String;
                                LV battery forces Hybrid. Mirrors the normal calculator. (user 2026-07-25) */}
                            <MiniLabel>{tr(lang, 'Inverter Type', '逆变器类型')}</MiniLabel>
                            <ToggleButtonGroup exclusive size="small" value={s.type || 'string'}
                                onChange={(_, v) => {
                                    if (!v) return;
                                    let b = s.brand || 'goodwe';
                                    if (v === 'hybrid' && (b === 'huawei' || b === 'solis')) { b = 'goodwe'; setSysField('brand', 'goodwe'); }
                                    setSysField('type', v);
                                    const k = Number(s.kwp) || 0;
                                    const ppk = commercialPricePerKwp(k, b, v, 0);
                                    if (ppk > 0 && k > 0) { setSysField('pricePerKwp', String(ppk)); setSysField('systemCost', String(Math.round(ppk * k))); }
                                }}
                                sx={{ mt: 0.3, '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.6rem', textTransform: 'none', py: 0.3, px: 0.85, borderRadius: '6px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' } }}>
                                <ToggleButton value="hybrid" disabled={isHvBattery(battery) || (isHuaweiBatt && Number(battery) > 0) || exceedsHybridMax(Number(s.kwp) || 0, RATIO_CAP_NO_BATT)}>{tr(lang, 'Hybrid', '混合')}</ToggleButton>
                                <ToggleButton value="string" disabled={Number(battery) > 0 && !isHuaweiBatt && !isHvBattery(battery) && !exceedsHybridMax(Number(s.kwp) || 0, RATIO_CAP_NO_BATT)}>{tr(lang, 'String', '串联')}</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <Box>
                            <MiniLabel>{tr(lang, 'Inverter Brand', '逆变器品牌')}</MiniLabel>
                            <ToggleButtonGroup exclusive size="small" value={s.brand || 'goodwe'}
                                onChange={(_, v) => {
                                    if (!v) return;
                                    setSysField('brand', v);
                                    // Huawei keeps whatever type is set — no String/Hybrid split.
                                    let t = s.type || 'string';
                                    if (v === 'solis') { t = 'string'; setSysField('type', 'string'); }
                                    if (v === 'goodwe') { t = 'hybrid'; setSysField('type', 'hybrid'); }
                                    const k = Number(s.kwp) || 0;
                                    const ppk = commercialPricePerKwp(k, v, t, 0);
                                    if (ppk > 0 && k > 0) { setSysField('pricePerKwp', String(ppk)); setSysField('systemCost', String(Math.round(ppk * k))); }
                                }}
                                sx={{ mt: 0.3, '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.6rem', textTransform: 'none', py: 0.3, px: 0.85, borderRadius: '6px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' } }}>
                                {/* Huawei has no String/Hybrid split (power module takes the battery),
                                    so it stays selectable under both types. (user 2026-09-26) */}
                                {[['goodwe', 'GoodWe'], ['solis', 'Solis'], ['huawei', 'Huawei']].map(([k, lbl]) => (
                                    <ToggleButton key={k} value={k} disabled={k === 'huawei' ? false : (k === 'goodwe' ? (s.type || 'string') === 'string' : (s.type || 'string') === 'hybrid')}>{lbl}</ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>
                        <Box>
                            <MiniLabel>{tr(lang, 'Panel Watt', '电板瓦数')}</MiniLabel>
                            <Select size="small" fullWidth value={Number(s.panelWatt) || 650}
                                onChange={(e) => {
                                    const w = Number(e.target.value);
                                    setSysField('panelWatt', String(w));
                                    const p = Number(s.panels) || 0;
                                    const _newKwp = p * w / 1000;
                                    if (_newKwp > 0) {
                                        setSysField('kwp', String(+_newKwp.toFixed(2)));
                                        const _price = priceForPanels(p, _newKwp, s.brand || 'goodwe', s.type || 'string');
                                        if (_price > 0) { setSysField('systemCost', String(Math.round(_price))); setSysField('pricePerKwp', String(Math.round(_price / _newKwp))); }
                                        setSysField('inverterKw', String(inverterFromKwp(_newKwp, RATIO_CAP_NO_BATT, s.type || 'string', s.brand)));
                                    }
                                }}
                                sx={MINI_INPUT_SX}>
                                {[620, 640, 650].map(w => <MenuItem key={w} value={w} sx={{ fontFamily: PP, fontSize: '0.72rem' }}>{w} Wp</MenuItem>)}
                            </Select>
                        </Box>
                        <Box>
                            <MiniLabel>{tr(lang, 'Battery kWh', '电池 kWh')}</MiniLabel>
                            <ToggleButtonGroup exclusive size="small" value={battBrand} fullWidth
                                onChange={(_, v) => {
                                    if (!v) return;
                                    setBattBrand(v);
                                    const snapped = snapBattToBrand(battery, v);   // keep the size valid for the new brand
                                    onBatteryBlur(snapped > 0 ? String(snapped) : '', v);
                                }}
                                sx={{ mt: 0.3, '& .MuiToggleButton-root': { flex: 1, fontFamily: PP, fontSize: '0.56rem', textTransform: 'none', py: 0.1, borderRadius: '6px !important' }, '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: '#fff !important', borderColor: 'transparent !important' } }}>
                                <ToggleButton value={BATT_BRAND_GOODWE}>GoodWe</ToggleButton>
                                <ToggleButton value={BATT_BRAND_HUAWEI}>Huawei</ToggleButton>
                            </ToggleButtonGroup>
                            {/* Stepper: 0 → LV 16-kWh (Hybrid) → HV 261-kWh units: 261/522/783… (String). (user 2026-07-10; 100 removed 2026-07-27) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                                {[['−', prevBattStep], ['+', nextBattStep]].map(([sym, fn], i) => (
                                    <Button key={i} size="small" variant="outlined" onClick={() => onBatteryBlur(String(fn(battery, battBrand)))}
                                        sx={{ minWidth: 24, width: 24, height: 30, p: 0, fontSize: '0.95rem', lineHeight: 1, order: i === 0 ? 0 : 2 }}>{sym}</Button>
                                ))}
                                <Box sx={{ flex: 1, order: 1, textAlign: 'center', fontFamily: PP, fontSize: '0.66rem', fontWeight: 700 }}>
                                    {Number(battery) > 0 ? `${battery}${(isHuaweiBatt || isHvBattery(battery)) ? ' HV' : ''}` : tr(lang, 'None', '无')}
                                </Box>
                            </Box>
                        </Box>
                        <Box>
                            <MiniLabel>{tr(lang, 'Per kWp (RM)', '每 kWp (RM)')}</MiniLabel>
                            <TextField size="small" type="number" fullWidth value={s.pricePerKwp ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setSysField('pricePerKwp', v);
                                    setSysField('systemCost', v && Number(s.kwp) ? String(Math.round(Number(v) * Number(s.kwp))) : '');
                                }}
                                inputProps={{ min: 0 }} sx={MINI_INPUT_SX} />
                        </Box>
                        <Box>
                            <MiniLabel>{tr(lang, 'System Price (RM)', '系统价 (RM)')}</MiniLabel>
                            <TextField size="small" type="number" fullWidth value={s.systemCost ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setSysField('systemCost', v);
                                    setSysField('pricePerKwp', v && Number(s.kwp) ? String(Math.round(Number(v) / Number(s.kwp))) : '');
                                }}
                                inputProps={{ min: 0 }} sx={MINI_INPUT_SX} />
                        </Box>
                        <Box>
                            <MiniLabel>{tr(lang, 'Add-on (RM)', '配件 (RM)')}</MiniLabel>
                            <TextField size="small" type="number" fullWidth value={s.addOn ?? ''}
                                onChange={(e) => setSysField('addOn', e.target.value)}
                                inputProps={{ min: 0 }} sx={MINI_INPUT_SX} />
                        </Box>
                    </Box>

                    {/* Residential-band Astern Discount checkbox — flat RM2,200, reduces the system price.
                        Only shown for meters at or under 30 kWp (ATAP residential pricing). (user 2026-08-07) */}
                    {asternDiscForMeter() > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <FormControlLabel
                                control={<Checkbox size="small" checked={asternOn} onChange={(e) => setAsternOn(e.target.checked)} sx={{ py: 0.25 }} />}
                                label={<Typography sx={{ fontSize: '0.72rem', fontWeight: 600, fontFamily: PP, color: asternOn ? '#0F766E' : COLORS.text }}>
                                    {tr(lang, `Astern Discount (RM ${asternDiscForMeter().toLocaleString()})`, `Astern 折扣 (RM ${asternDiscForMeter().toLocaleString()})`)}
                                </Typography>} />
                        </Box>
                    )}

                    {/* Catalogue add-on rows — same picker as single-meter CSC (cscCombinedTab.CardAddons),
                        light-themed to match this meter row. Folds into cardPricing alongside the flat
                        Add-on (RM) field above, and into the meter's create-sale line items. */}
                    <Box sx={{ mt: 1.25 }}>
                        <MiniLabel>{tr(lang, 'Catalogue Add-ons', '配件目录')}</MiniLabel>
                        <Box sx={{ bgcolor: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 1.5, p: 1 }}>
                            {effAddonRows().length === 0 && (
                                <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP, fontStyle: 'italic', textAlign: 'center', py: 0.5 }}>
                                    {tr(lang, 'No add-ons yet — pick from catalogue below', '暂无配件 — 从下方目录选择')}
                                </Typography>
                            )}
                            {effAddonRows().map((r) => {
                                if (r._autoInvUpg) {
                                    const aline = computeAddonAmount(r, 1);
                                    return (
                                        <Box key={r.rowId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: `1px dashed ${COLORS.border}` }}>
                                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.text, fontFamily: PP, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.itemName}>
                                                {r.itemName} <span style={{ fontSize: '0.56rem', color: COLORS.sub, fontWeight: 700 }}>· AUTO</span>
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.66rem', color: COLORS.success, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, flexShrink: 0, ml: 1 }}>= RM {aline.total.toLocaleString()}</Typography>
                                        </Box>
                                    );
                                }
                                const qtyBearing = isQtyBearing(r.unitType);
                                const qtyShown = r._qty != null ? r._qty : 1;
                                const line = computeAddonAmount(r, Number(qtyShown) || 0);
                                const unitTag = unitSuffix(r.unitType);
                                const cleaned = String(r.price ?? '').replace(/[^\d.+]/g, '');
                                let baseStr = '', rateStr = '';
                                if (cleaned.includes('+')) { const p = cleaned.split('+'); baseStr = p[0] || ''; rateStr = p[1] || ''; }
                                else { rateStr = cleaned; }
                                const setPriceParts = (nb, nr) => {
                                    const b = (nb || '').toString().replace(/[^\d.]/g, '');
                                    const rt = (nr || '').toString().replace(/[^\d.]/g, '');
                                    const combined = (parseFloat(b) > 0) ? (rt === '' ? b : `${b}+${rt}`) : rt;
                                    updateAddonRow(r.rowId, { price: combined });
                                };
                                return (
                                    <Box key={r.rowId} sx={{ display: 'grid', gridTemplateColumns: '1fr 56px 80px 24px', gap: 0.5, alignItems: 'center', py: 0.5, borderBottom: `1px dashed ${COLORS.border}` }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.text, fontFamily: PP, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.itemName}>{r.itemName}</Typography>
                                                {unitTag && <Typography sx={{ fontSize: '0.6rem', color: COLORS.sub, fontFamily: PP, fontWeight: 600, flexShrink: 0 }}>{unitTag}</Typography>}
                                            </Box>
                                            {line.total > 0 && (
                                                <Typography sx={{ fontSize: '0.6rem', color: COLORS.success, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, mt: 0.1 }}>
                                                    {(line.base > 0 && line.perUnit > 0)
                                                        ? `RM ${line.base.toLocaleString()} + ${qtyShown}× RM ${line.perUnit.toLocaleString()} = RM ${line.total.toLocaleString()}`
                                                        : `= RM ${line.total.toLocaleString()}`}
                                                </Typography>
                                            )}
                                        </Box>
                                        <TextField size="small" type="number" value={r._qty ?? 1} disabled={!qtyBearing}
                                            onChange={(e) => updateAddonRow(r.rowId, { _qty: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 })}
                                            inputProps={{ step: 1, min: 0 }} sx={MINI_INPUT_SX} />
                                        <TextField size="small" type="number" value={rateStr} placeholder="0"
                                            onChange={(e) => setPriceParts(baseStr, e.target.value)} inputProps={{ step: 1, min: 0 }} sx={MINI_INPUT_SX} />
                                        <Button size="small" onClick={() => removeAddonRow(r.rowId)}
                                            sx={{ minWidth: 0, width: 22, height: 22, p: 0, fontSize: '0.85rem', color: COLORS.sub, '&:hover': { color: '#EF4444', bgcolor: 'rgba(239,68,68,0.08)' } }}>×</Button>
                                    </Box>
                                );
                            })}
                            <Box sx={{ mt: 0.75, pt: 0.5 }}>
                                <Select size="small" fullWidth displayEmpty value=""
                                    onChange={(e) => { if (e.target.value) addAddonRow(Number(e.target.value)); }}
                                    sx={{ fontFamily: PP, fontSize: '0.74rem', borderRadius: '6px' }}>
                                    <MenuItem value="" disabled sx={{ fontSize: '0.72rem', fontFamily: PP, fontStyle: 'italic' }}>+ {tr(lang, 'Add from catalogue…', '从目录添加配件…')}</MenuItem>
                                    {(() => {
                                        const groups = {};
                                        (addonCatalog || []).forEach((a) => { const c = a.category || 'General'; (groups[c] = groups[c] || []).push(a); });
                                        const nodes = [];
                                        Object.keys(groups).sort().forEach((cat) => {
                                            nodes.push(<MenuItem key={`h-${cat}`} disabled sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.sub, opacity: '1 !important', fontFamily: PP }}>{cat}</MenuItem>);
                                            groups[cat].forEach((a) => {
                                                const priceNum = parseFloat(String(a.price ?? '').replace(/[^\d.+]/g, ''));
                                                const priceLabel = String(a.price ?? '').includes('+') ? a.price : (Number.isFinite(priceNum) ? priceNum.toLocaleString() : a.price);
                                                const unitTag = unitSuffix(a.unitType);
                                                nodes.push(
                                                    <MenuItem key={a.addonId} value={a.addonId} sx={{ fontSize: '0.72rem', fontFamily: PP }}>
                                                        {a.itemName} — RM {priceLabel}{unitTag}
                                                    </MenuItem>
                                                );
                                            });
                                        });
                                        return nodes;
                                    })()}
                                </Select>
                            </Box>
                        </Box>
                    </Box>

                    {/* System vs Battery vs Add-on split (clear breakdown) */}
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, fontFamily: PP }}>
                        <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP }}>
                            {tr(lang, 'System', '系统')}: <b style={{ color: COLORS.text }}>{fmtRM(P.panel)}</b>
                        </Typography>
                        <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP }}>
                            {tr(lang, 'Battery', '电池')}: <b style={{ color: COLORS.text }}>{fmtRM(P.battery)}</b>
                        </Typography>
                        <Typography sx={{ fontSize: '0.66rem', color: COLORS.sub, fontFamily: PP }}>
                            {tr(lang, 'Add-on', '配件')}: <b style={{ color: COLORS.text }}>{fmtRM(P.addons)}</b>
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Compact result strip (so each meter shows its own sizing) */}
            {optB && (parseFloat(bill) > 0) ? (
                <Box sx={{ m: 1.5, mt: 0, borderRadius: 2, p: 1.25, background: 'linear-gradient(135deg,#064e3b,#065f46)' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', rowGap: 1 }}>
                        <Spec label="kWp" value={s.kwp} />
                        <Spec label={tr(lang, 'Panels', '电板')} value={s.panels} />
                        <Spec label={tr(lang, 'Inverter', '逆变器')} value={`${s.inverterKw}kW`} />
                        <Spec label={tr(lang, 'Battery', '电池')} value={`${battery}kWh`} />
                        <Spec label={tr(lang, 'Saving/mo', '每月省')} value={fmtRM(result.savingMo)} />
                        <Spec label="CASH" value={fmtRM(result.cash)} />
                        <Spec label="ROI" value={result.roiYears != null ? `${result.roiYears}y` : '—'} />
                    </Box>
                </Box>
            ) : (
                <Typography sx={{ px: 1.5, pb: 1.5, fontSize: '0.74rem', color: COLORS.sub, fontFamily: PP, fontStyle: 'italic' }}>
                    {tr(lang, 'Enter a bill to size this meter.', '输入账单以计算此电表。')}
                </Typography>
            )}
        </Box>
    );
}
